import { Button, Stack, type Theme, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '/@/components/bayer/alert';
import {
    useAcceptAdvisorRvInvitation,
    useAdvisorRvInvitations,
    useAdvisorRvLinks,
    useDeclineAdvisorRvInvitation,
    useDistributorsAndAdvisors,
} from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY } from '.';
import RVOnboardingModalAdvisorSelector from './advisor-selector';
import RVOnboardingModalInitial from './initial';
import RVOnboardingModalNewInvitationsReceived from './new-invitations-received';
import RVOnboardingModalInvites from './pending-invites';

const RVOnboardingModalAndAlert = () => {
    // Check open modal + Alert to show
    const [open, setOpen] = useState(false);
    const advisorRvLinksQuery = useAdvisorRvLinks();
    const advisorsRvInvitationsQuery = useAdvisorRvInvitations();
    const pendingInvitationsQuery = useAdvisorRvInvitations({ status: 'pending', direction: 'received' });

    const hasLinkedAdvisors = (advisorRvLinksQuery.data?.results?.length ?? 0) > 0;
    const hasInvitedAdvisors = (advisorsRvInvitationsQuery.data?.results?.length ?? 0) > 0;
    const hasLinkedOrInvitedAdvisors = hasLinkedAdvisors || hasInvitedAdvisors;
    const wasOpened = localStorage.getItem(ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY) === 'true';

    useEffect(() => {
        if (advisorRvLinksQuery.isLoading || advisorsRvInvitationsQuery.isLoading) return;

        if (!hasLinkedOrInvitedAdvisors && !wasOpened) {
            setOpen(true);
            localStorage.setItem(ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY, 'true');
        }
    }, [wasOpened, advisorRvLinksQuery.isLoading, advisorsRvInvitationsQuery.isLoading, hasLinkedOrInvitedAdvisors]);

    useEffect(() => {
        if (wasOpened && (pendingInvitationsQuery?.data?.count || 0) > 0) {
            setStep('new-invitations-received');
            setOpen(true);
        }
    }, [wasOpened, pendingInvitationsQuery?.data?.count]);

    // Steps
    const [step, setStep] = useState<
        'initial' | 'advisor-selector' | 'pending-invites' | 'new-invitations-received' | 'sent-invites'
    >('initial');

    // Alert title and buttons dynamically depending on user situation
    const acceptInvitationMutation = useAcceptAdvisorRvInvitation();
    const declineInvitationMutation = useDeclineAdvisorRvInvitation();
    const distributorsAndAdvisorsQuery = useDistributorsAndAdvisors();

    const pendingInvitationsCount = pendingInvitationsQuery.data?.count ?? 0;
    const firstPendingInvitation = pendingInvitationsQuery.data?.results?.[0];

    const advisorDistributorNames = useMemo(() => {
        const map: Record<number, string> = {};
        const results = distributorsAndAdvisorsQuery.data?.results ?? [];
        for (const el of results) {
            const distributorName = el.userNode.name;
            for (const child of el.children) {
                const existing = map[child.id];
                map[child.id] = existing ? `${existing}, ${distributorName}` : distributorName;
            }
        }
        return map;
    }, [distributorsAndAdvisorsQuery.data?.results]);

    const alertTitle = useMemo(() => {
        if (pendingInvitationsCount === 1 && firstPendingInvitation) {
            const advisorName = firstPendingInvitation.advisor.name;
            const distributorName = advisorDistributorNames[firstPendingInvitation.advisor.id] ?? '';
            const parentPart = distributorName ? ` (${distributorName})` : '';
            return _('{{advisorName}}{{parentPart}} te envió una solicitud de vinculación.', {
                advisorName,
                parentPart,
            });
        }
        if (pendingInvitationsCount > 1) {
            return _('Tenés {{count}} solicitudes de vinculación pendientes de revisión.', {
                count: pendingInvitationsCount,
            });
        }

        return _('Gestioná tus solicitudes de vinculación.');
    }, [pendingInvitationsCount, firstPendingInvitation, advisorDistributorNames]);

    const handleAcceptSingleInvitation = useCallback(() => {
        if (firstPendingInvitation) {
            acceptInvitationMutation.trigger(firstPendingInvitation.id);
        }
    }, [firstPendingInvitation, acceptInvitationMutation]);

    const handleDeclineSingleInvitation = useCallback(() => {
        if (firstPendingInvitation) {
            declineInvitationMutation.trigger(firstPendingInvitation.id);
        }
    }, [firstPendingInvitation, declineInvitationMutation]);

    const alertButtons = useMemo(() => {
        if (pendingInvitationsCount === 1) {
            return [
                <Button
                    key='decline'
                    variant='text'
                    size='small'
                    color='secondary'
                    onClick={handleDeclineSingleInvitation}
                    disabled={declineInvitationMutation.isMutating}
                    sx={{ border: (theme: Theme) => `1px solid ${theme.palette.primary.light}` }}
                >
                    {_('Rechazar')}
                </Button>,
                <Button
                    key='accept'
                    variant='contained'
                    color='secondary'
                    onClick={handleAcceptSingleInvitation}
                    disabled={acceptInvitationMutation.isMutating}
                >
                    {_('Aceptar')}
                </Button>,
            ];
        }
        if (pendingInvitationsCount > 1) {
            return [
                <Button
                    key='view'
                    onClick={() => {
                        setStep('pending-invites');
                        setOpen(true);
                    }}
                    variant='text'
                    size='small'
                    color='secondary'
                    sx={{ border: (theme: Theme) => `1px solid ${theme.palette.primary.light}` }}
                >
                    {_('Ver solicitudes')}
                </Button>,
            ];
        }
        return [
            <Button
                key='view'
                variant='text'
                color='primary'
                onClick={() => {
                    setStep('pending-invites');
                    setOpen(true);
                }}
            >
                {_('Ver solicitudes')}
            </Button>,
            <Button
                key='send'
                variant='text'
                size='small'
                color='secondary'
                onClick={() => {
                    setStep('advisor-selector');
                    setOpen(true);
                }}
                sx={{ border: (theme: Theme) => `1px solid ${theme.palette.primary.light}` }}
            >
                {_('Enviar nueva solicitud')}
            </Button>,
        ];
    }, [
        pendingInvitationsCount,
        acceptInvitationMutation.isMutating,
        declineInvitationMutation.isMutating,
        handleAcceptSingleInvitation,
        handleDeclineSingleInvitation,
    ]);

    if (advisorRvLinksQuery.isLoading || advisorsRvInvitationsQuery.isLoading) return null;

    return (
        <>
            {/* Alert  */}
            <Alert
                severity='blue'
                sx={{
                    alignItems: 'center',
                    my: 1,
                    '& .MuiAlert-message': {
                        display: 'flex',
                        alignItems: 'center',
                        my: 'auto',
                        justifyContent: 'space-between',
                        width: '100%',
                    },
                }}
            >
                <Typography>{alertTitle}</Typography>
                <Stack direction='row' spacing={2} alignItems='center'>
                    {alertButtons}
                </Stack>
            </Alert>

            {/* Modal */}
            {/* Main Flow */}
            {step === 'initial' && !hasLinkedOrInvitedAdvisors && (
                <RVOnboardingModalInitial
                    isRV
                    open={open}
                    onClose={() => setOpen(false)}
                    onNext={() => {
                        if ((pendingInvitationsQuery?.data?.count || 0) > 0) {
                            setStep('new-invitations-received');
                        }
                        setStep('advisor-selector');
                    }}
                />
            )}
            {step === 'new-invitations-received' && (
                <RVOnboardingModalNewInvitationsReceived
                    pendingInvitationsQuery={pendingInvitationsQuery}
                    open={open}
                    onClose={() => setOpen(false)}
                    onInviteMoreAdvisors={() => setStep('advisor-selector')}
                />
            )}
            {step === 'advisor-selector' && <RVOnboardingModalAdvisorSelector open={open} setOpen={setOpen} />}

            {/* Secondary Flow */}
            {step === 'pending-invites' && (
                <RVOnboardingModalInvites
                    open={open}
                    onClose={() => setOpen(false)}
                    onInviteMoreAdvisors={() => setStep('advisor-selector')}
                />
            )}
        </>
    );
};
export default RVOnboardingModalAndAlert;
